import { ref, computed } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import { getOrganizerRefundPreview, issueRegistrationRefund } from '@/api/participants'
import type { OrganizerRefundPreview } from '@/types/participants'
import type { OrganizerRefund } from '@/types/refunds'

export interface IssueRefundResult {
  refund: OrganizerRefund
  /** True when the provider outcome is unknown (HTTP 202): the refund shows as Processing. */
  unconfirmed: boolean
}

export interface AmountError {
  /** i18n key. */
  key: string
  params?: Record<string, string>
}

export interface RefundFieldErrors {
  amount?: string
  reason?: string
}

interface ApiErrorBody {
  message?: string
  error?: string
  errors?: Record<string, string[]>
}

function isApiError(e: unknown): e is { response: { status: number; data: ApiErrorBody } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

/** Parses a user-entered decimal into integer cents. Null when it is not a plain decimal with at most 2 decimals. */
export function toCents(value: string): number | null {
  const trimmed = value.trim().replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null
  const [whole = '0', fraction = ''] = trimmed.split('.')
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
}

export function fromCents(cents: number): string {
  const safe = Math.max(0, Math.round(cents))
  return `${Math.floor(safe / 100)}.${String(safe % 100).padStart(2, '0')}`
}

export const REASON_MAX_LENGTH = 1000

export interface UseRegistrationRefundReturn {
  preview: Ref<OrganizerRefundPreview | null>
  amount: Ref<string>
  reason: Ref<string>
  loadingPreview: Ref<boolean>
  previewError: Ref<boolean>
  submitting: Ref<boolean>
  fieldErrors: Ref<RefundFieldErrors>
  /** Message already translated by the server (409 / 422 refund_rejected / 502), when present. */
  formError: Ref<string | null>
  /** i18n key used when the server supplied no message. */
  formErrorKey: Ref<string | null>
  remainingAfter: ComputedRef<string>
  amountError: ComputedRef<AmountError | null>
  canSubmit: ComputedRef<boolean>
  fetchPreview: (registrationId: number) => Promise<void>
  /** Sets the amount to `prefill` capped at the remaining amount, or to the remaining amount. */
  applyDefaultAmount: (prefill: string | null) => void
  submit: (registrationId: number) => Promise<IssueRefundResult | null>
  reset: () => void
}

export function useRegistrationRefund(): UseRegistrationRefundReturn {
  const preview = ref<OrganizerRefundPreview | null>(null)
  const amount = ref('')
  const reason = ref('')
  const loadingPreview = ref(false)
  const previewError = ref(false)
  const submitting = ref(false)
  const fieldErrors = ref<RefundFieldErrors>({})
  const formError = ref<string | null>(null)
  const formErrorKey = ref<string | null>(null)

  const remainingCents = computed<number>(() =>
    preview.value === null ? 0 : (toCents(preview.value.refundable_remaining) ?? 0),
  )

  const remainingAfter = computed<string>(() => {
    const entered = toCents(amount.value) ?? 0
    return fromCents(Math.max(0, remainingCents.value - entered))
  })

  const amountError = computed<AmountError | null>(() => {
    if (amount.value.trim() === '') return { key: 'participants.refunds.amount_required' }
    const cents = toCents(amount.value)
    if (cents === null) {
      return /^\d+([.,]\d+)?$/.test(amount.value.trim())
        ? { key: 'participants.refunds.amount_decimals' }
        : { key: 'participants.refunds.amount_required' }
    }
    if (cents < 1) return { key: 'participants.refunds.amount_too_low' }
    if (cents > remainingCents.value) {
      return {
        key: 'participants.refunds.amount_too_high',
        params: { amount: fromCents(remainingCents.value) },
      }
    }
    return null
  })

  const canSubmit = computed<boolean>(
    () =>
      preview.value !== null &&
      preview.value.blocked_reason === null &&
      amountError.value === null &&
      reason.value.length <= REASON_MAX_LENGTH &&
      !submitting.value,
  )

  function clearErrors(): void {
    fieldErrors.value = {}
    formError.value = null
    formErrorKey.value = null
  }

  async function fetchPreview(registrationId: number): Promise<void> {
    loadingPreview.value = true
    previewError.value = false
    try {
      preview.value = await getOrganizerRefundPreview(registrationId)
    } catch {
      previewError.value = true
    } finally {
      loadingPreview.value = false
    }
  }

  function applyDefaultAmount(prefill: string | null): void {
    if (preview.value === null) return
    const remaining = remainingCents.value
    const prefillCents = prefill === null ? null : toCents(prefill)
    amount.value = fromCents(prefillCents === null ? remaining : Math.min(prefillCents, remaining))
  }

  async function submit(registrationId: number): Promise<IssueRefundResult | null> {
    if (!canSubmit.value) return null
    submitting.value = true
    clearErrors()

    const cents = toCents(amount.value)
    const trimmedReason = reason.value.trim()

    try {
      return await issueRegistrationRefund(registrationId, {
        amount: fromCents(cents ?? 0),
        reason: trimmedReason === '' ? null : trimmedReason,
      })
    } catch (e: unknown) {
      if (!isApiError(e)) {
        formErrorKey.value = 'common.error_generic'
        return null
      }
      const { status, data } = e.response
      if (status === 422 && data.errors !== undefined) {
        fieldErrors.value = {
          amount: data.errors['amount']?.[0],
          reason: data.errors['reason']?.[0],
        }
        if (fieldErrors.value.amount === undefined && fieldErrors.value.reason === undefined) {
          // No field we render inline: surface a general error instead of failing silently.
          formError.value = data.message ?? null
          if (formError.value === null) formErrorKey.value = 'common.error_generic'
        }
      } else if (status === 422 && data.error === 'refund_rejected') {
        formError.value = data.message ?? null
        if (formError.value === null) formErrorKey.value = 'participants.refunds.error_rejected_fallback'
      } else if (status === 409) {
        formError.value = data.message ?? null
        if (formError.value === null) formErrorKey.value = 'common.error_generic'
        await fetchPreview(registrationId)
      } else if (status === 502) {
        formError.value = data.message ?? null
        if (formError.value === null) formErrorKey.value = 'participants.refunds.error_provider_unavailable'
      } else if (status === 403) {
        formErrorKey.value = 'cancellation_policy.error_not_authorised'
      } else {
        formErrorKey.value = 'common.error_generic'
      }
      return null
    } finally {
      submitting.value = false
    }
  }

  function reset(): void {
    preview.value = null
    amount.value = ''
    reason.value = ''
    loadingPreview.value = false
    previewError.value = false
    submitting.value = false
    clearErrors()
  }

  return {
    preview,
    amount,
    reason,
    loadingPreview,
    previewError,
    submitting,
    fieldErrors,
    formError,
    formErrorKey,
    remainingAfter,
    amountError,
    canSubmit,
    fetchPreview,
    applyDefaultAmount,
    submit,
    reset,
  }
}
